import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import styled, { css } from 'styled-components';

import type ApiState from '@api-client/state';
import type { Types } from '@common/models';
import type { ScopedTalknClient } from '@api-client/TalknAPI';

import Article, { ArticleType } from '../molecules/Article';
import { H5 } from '../flexes';
import styles from '../../styles';
import { convertRanksToArticles } from '../../utils/convertRankToArticle';

type ArticleOrderType = {
  talknAPI: ScopedTalknClient;
  state: ApiState;
  root: Element;
  maxItems: number;
  showEmpty: boolean;
};

const ArticleOrderComponent: React.FC<ArticleOrderType> = (props) => {
  const { talknAPI, state, root, maxItems, showEmpty } = props;
  const { rankAll, tuneCh } = state;
  
  // Persistent meta cache: retains meta across rankAll updates
  // (broadcast updates from subscribe handler don't include meta)
  const metaMapRef = useRef(new Map<string, Types['ChDetail']>());

  // Convert Rank[] to ArticleType[] using rankAll
  const articles = useMemo(() => {
    const metaMap = metaMapRef.current;

    // Add meta from tuneCh if available
    if (tuneCh.meta && tuneCh.meta.connection) {
      metaMap.set(tuneCh.meta.connection, tuneCh.meta);
    }

    // Add/update meta from rankAll entries (only when present)
    rankAll.forEach((rankItem) => {
      if (rankItem.meta && rankItem.connection) {
        metaMap.set(rankItem.connection, rankItem.meta);
      }
    });

    // Exclude the current channel; only show channels within the same subtree (tuneCh.connection prefix)
    const prefix = tuneCh.connection ?? '';
    let filtered = rankAll.filter(
      (r) => r.connection !== tuneCh.connection && (prefix === '' || r.connection.startsWith(prefix))
    );
    if (maxItems > 0) {
      filtered = filtered.slice(0, maxItems);
    }
    return convertRanksToArticles(filtered, metaMap);
  }, [rankAll, tuneCh.meta, tuneCh.connection]);
  
  const { connection: ch } = tuneCh;
  const orderRef = useRef<HTMLOListElement>(null);
  const [active, setActive] = useState<boolean>(false);
  const [focusIndex, setFocusIndex] = useState<undefined | number>(undefined);
  const [articleHeights, setArticleHeights] = useState<number[]>([0]);

  const handleOnScroll = () => {
    setActive(true);
  };

  const handleOnMouseOverContainer = () => {
    const rootElm = root as HTMLElement;
    rootElm.style.zIndex = String(Number.MAX_SAFE_INTEGER);
    rootElm.style.position = 'relative';
  };
  const handleOnMouseOver = () => {
    setActive(true);
  };
  const handleOnMouseMove = () => {
    setActive(true);
  };
  const handleOnMouseLeave = () => {
    const rootElm = root as HTMLElement;
    rootElm.style.zIndex = 'auto';
    setFocusIndex(undefined);
    setActive(false);
  };

  const handleOnMouseLeaveArrowButton = () => {
    setActive(false);
  };

  const handleOnButtonClick = (direction: 'left' | 'right') => {
    const orderElm = orderRef.current;
    if (!orderElm) return;
    const listElm = orderElm.children[0] as HTMLElement;
    if (!listElm) return;
    const currentLeft = orderRef.current.scrollLeft;
    const addScrollLeft = direction === 'left' ? -listElm.clientWidth : listElm.clientWidth;

    orderRef.current.scrollTo({
      left: currentLeft + addScrollLeft,
      behavior: 'smooth',
    });
  };

  const activeOnEvents = { onMouseMove: handleOnMouseMove, onMouseOver: handleOnMouseOver, onTouchStart: handleOnMouseOver };

  const measureArticleHeights = useCallback(() => {
    if (!orderRef.current) return;
    const heights: number[] = [];
    for (let i = 0; i < articles.length; i++) {
      const articleLi = orderRef.current.children[i] as HTMLElement;
      const articleElm = articleLi?.querySelector('a');
      heights.push(articleElm ? articleElm.scrollHeight * styles.articleOpenScale : 0);
    }
    setArticleHeights(heights);
  }, [articles.length]);

  useEffect(() => {
    measureArticleHeights();
    window.addEventListener('resize', measureArticleHeights);
    return () => window.removeEventListener('resize', measureArticleHeights);
  }, [measureArticleHeights]);

  // rankScopeルートのtitleを取得
  const [rankScopeTitle, setRankScopeTitle] = useState('');
  useEffect(() => {
    const rankScopeConnection = `/${window.location.hostname}/`;
    if (window.TalknAPI) {
      window.TalknAPI(rankScopeConnection).then((client: ScopedTalknClient) => {
        // metaは非同期で取得されるため、定期的にチェック
        let attempts = 0;
        const check = setInterval(() => {
          const meta = client.getState().tuneCh?.meta;
          if (meta?.title && meta.title !== 'talkn') {
            setRankScopeTitle(meta.title);
            clearInterval(check);
          }
          if (++attempts > 20) clearInterval(check); // 10秒で諦め
        }, 500);
      }).catch(() => {});
    }
  }, []);
  const tuneChTitle = tuneCh.meta?.title && tuneCh.meta.title !== 'talkn' ? tuneCh.meta.title : '';
  const threadTitle = tuneChTitle || rankScopeTitle || window.location.hostname || ch;
  if (articles.length === 0) {
    if (!showEmpty) return null;
    return (
      <Container $focusHeight={undefined}>
        <TitleCustom id={'ArticleOrder'}>{threadTitle}</TitleCustom>
        <EmptyState>コンテンツなし</EmptyState>
      </Container>
    );
  }

  return (
    <Container $focusHeight={articleHeights[focusIndex ?? -1]} onMouseOver={handleOnMouseOverContainer} onMouseLeave={handleOnMouseLeave}>

      <TitleCustom id={'ArticleOrder'}>
        {threadTitle}
      </TitleCustom>
      <ArrowRightButton
        $active={active}
        {...activeOnEvents}
        onClick={() => handleOnButtonClick('left')}
        onMouseLeave={handleOnMouseLeaveArrowButton}
      />
      <ArticleOrder
        ref={orderRef}
        $focusHeight={articleHeights[focusIndex ?? -1]}
        onScroll={handleOnScroll}
        onMouseLeave={handleOnMouseLeave}
        {...activeOnEvents}>
        {articles.map((article, index) => (
          <ArticleList key={`${article.ch}.${index}`}>
            <Article talknAPI={talknAPI} article={article} index={index} focusIndex={focusIndex} setFocusIndex={setFocusIndex} />
          </ArticleList>
        ))}
      </ArticleOrder>
      <ArrowLeftButton
        $active={active}
        {...activeOnEvents}
        onClick={() => handleOnButtonClick('right')}
        onMouseLeave={handleOnMouseLeaveArrowButton}
      />
    </Container>
  );
};

export default ArticleOrderComponent;

type ContainerPropeType = {
  $focusHeight?: number;
};

const Container = styled.div<ContainerPropeType>`
  display: flex;
  flex-flow: column wrap;
  width: 100%;
  margin-top: 0;
  margin-right: 0;
  margin-bottom: ${(props) =>
    props.$focusHeight === undefined ? '0' : `-${props.$focusHeight + styles.doubleMargin - styles.articleOrderHeight}px`};
  margin-left: 0;
  border: 0;
  overflow-y: visible;
  transform: translate(0px, 0px);
  font-size: var(--talkn-title-font-size, 16px);
  font-weight: var(--talkn-title-font-weight, 300);
  color: var(--talkn-font-color, ${styles.fontColor});
  font-family: var(--talkn-font-family, inherit);
  * {
    text-decoration: none;
    font-family: var(--talkn-font-family, inherit);
  }
`;

type ArticleOrderPropeType = {
  $focusHeight?: number;
};

const ArticleOrder = styled.ol<ArticleOrderPropeType>`
  display: flex;
  flex-flow: row nowrap;
  width: inherit;
  max-width: inherit;
  height: ${(props) => (props.$focusHeight === undefined
    ? `var(--talkn-order-height, ${styles.articleOrderHeight}px)`
    : `${props.$focusHeight + styles.doubleMargin}px`)};
  margin: 0;
  border: 0;
  overflow-x: scroll;
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const TitleCustom = styled.div`
  display: flex;
  align-items: center;
  height: var(--talkn-title-height, 40px);
  padding: 0 ${styles.doublePadding}px;
  text-indent: 32px;
  font-size: var(--talkn-title-font-size, 16px);
  font-weight: var(--talkn-title-font-weight, 300);
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--talkn-order-height, ${styles.articleOrderHeight}px);
  color: var(--talkn-font-color, ${styles.fontColor});
  font-size: var(--talkn-desc-font-size, 90%);
  font-weight: 200;
  opacity: 0.5;
`;

const ArticleList = styled.li`
  padding: var(--talkn-card-gap, ${styles.basePadding}px);
  scroll-snap-align: start;
  list-style: none;
  transform: translate(0px, 0px);
`;

type ArrowButtonPropsType = {
  $active: boolean;
};
const H3LineHeight = styles.doubleMargin;
const H3Margin = styles.baseMargin;
const H3Height = H3LineHeight + H3Margin * 2;
const ArrowCommonCss = css<ArrowButtonPropsType>`
  z-index: 1;
  position: absolute;
  top: 42px;
  display: var(--talkn-arrow-display, flex);
  flex-flow: row wrap;
  align-items: center;
  justify-content: center;
  width: ${styles.quadSize}px;
  height: var(--talkn-order-height, ${styles.articleOrderHeight}px);
  padding: 0;
  margin: 0;
  background-color: ${(props) => (props.$active ? 'var(--talkn-arrow-bg-hover, rgba(0, 0, 0, 0.6))' : 'var(--talkn-arrow-bg, rgba(0, 0, 0, 0.3))')};
  background-position: 50%;
  background-repeat: no-repeat;
  color: transparent;
  border: 0;
  outline: 0 none;
  transition: opacity 300ms ease, background-color 300ms ease;
`;

const ArrowRightButton = styled.button<ArrowButtonPropsType>`
  ${ArrowCommonCss};
  border-radius: 0 var(--talkn-border-radius, 10px) var(--talkn-border-radius, 10px) 0;
  left: 0;
  background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTMuMjM3IDE3LjIzN3YtMi40NzRsMTQgMTRjLjY4NC42ODMuNjg0IDEuNzkgMCAyLjQ3NGExLjc0OCAxLjc0OCAwIDAxLTIuNDc0IDBsLTE0LTE0YTEuNzQ4IDEuNzQ4IDAgMDEwLTIuNDc0bDE0LTE0YTEuNzQ4IDEuNzQ4IDAgMDEyLjQ3NCAwYy42ODQuNjgzLjY4NCAxLjc5IDAgMi40NzRsLTE0IDE0eiIgZmlsbD0iI0VGRjFGMSIvPjwvc3ZnPg==);
`;

const ArrowLeftButton = styled.button<ArrowButtonPropsType>`
  ${ArrowCommonCss};
  border-radius: var(--talkn-border-radius, 10px) 0 0 var(--talkn-border-radius, 10px);
  right: 0;
  background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTE0Ljc2MyAxNy4yMzd2LTIuNDc0bC0xNCAxNGExLjc0OCAxLjc0OCAwIDAwMCAyLjQ3NGMuNjgzLjY4NCAxLjc5LjY4NCAyLjQ3NCAwbDE0LTE0YTEuNzQ4IDEuNzQ4IDAgMDAwLTIuNDc0bC0xNC0xNEExLjc1IDEuNzUgMCAwMC43NjMgMy4yMzdsMTQgMTR6IiBmaWxsPSIjRUZGMUYxIi8+PC9zdmc+);
`;
